import React, { useState, useEffect } from 'react';

interface CashBurstProps {
  amount: number;
  trigger: boolean;
  onComplete?: () => void;
  centerX?: number;
  centerY?: number;
}

interface Particle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  rotationSpeed: number;
  scale: number;
  opacity: number;
  color: string;
  emoji: string;
}

const CashBurst: React.FC<CashBurstProps> = ({ 
  amount, 
  trigger, 
  onComplete,
  centerX = 50,
  centerY = 50
}) => {
  const [particles, setParticles] = useState<Particle[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);
  
  const emojis = ['💰', '💎', '🪙', '💵', '✨', '⭐', '🎉'];
  const colors = ['#FFD700', '#FFA500', '#FF6B35', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'];
  
  useEffect(() => {
    if (trigger && !isAnimating) {
      createParticles();
    }
  }, [trigger, isAnimating]);
  
  const createParticles = () => {
    setIsAnimating(true);
    
    // Create burst particles
    const newParticles: Particle[] = [];
    const particleCount = Math.min(Math.max(amount / 5, 8), 20); // Scale with amount
    
    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * 2 * Math.PI + (Math.random() - 0.5) * 0.5;
      const speed = 3 + Math.random() * 4;
      const distance = Math.random() * 100 + 50;
      
      newParticles.push({
        id: i,
        x: centerX,
        y: centerY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 2, // Slight upward bias
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 20,
        scale: 0.8 + Math.random() * 0.4,
        opacity: 1,
        color: colors[Math.floor(Math.random() * colors.length)],
        emoji: emojis[Math.floor(Math.random() * emojis.length)]
      });
    }
    
    setParticles(newParticles);
    
    // Play sound effect
    playBurstSound();
    
    // Animate particles
    animateParticles(newParticles);
  };
  
  const playBurstSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Create a celebratory burst sound
      const oscillator1 = audioContext.createOscillator();
      const oscillator2 = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      const filter = audioContext.createBiquadFilter();
      
      // Triumphant chord progression
      oscillator1.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
      oscillator1.frequency.exponentialRampToValueAtTime(659.25, audioContext.currentTime + 0.1); // E5
      oscillator1.frequency.exponentialRampToValueAtTime(783.99, audioContext.currentTime + 0.3); // G5
      
      oscillator2.frequency.setValueAtTime(261.63, audioContext.currentTime); // C4
      oscillator2.frequency.exponentialRampToValueAtTime(329.63, audioContext.currentTime + 0.1); // E4
      oscillator2.frequency.exponentialRampToValueAtTime(392.00, audioContext.currentTime + 0.3); // G4
      
      oscillator1.type = 'sine';
      oscillator2.type = 'triangle';
      
      filter.type = 'bandpass';
      filter.frequency.setValueAtTime(800, audioContext.currentTime);
      filter.frequency.exponentialRampToValueAtTime(1200, audioContext.currentTime + 0.2);
      
      // Triumphant envelope
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + 0.02);
      gainNode.gain.linearRampToValueAtTime(0.12, audioContext.currentTime + 0.1);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.6);
      
      oscillator1.connect(filter);
      oscillator2.connect(filter);
      filter.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      const now = audioContext.currentTime;
      oscillator1.start(now);
      oscillator2.start(now);
      oscillator1.stop(now + 0.6);
      oscillator2.stop(now + 0.6);
    } catch (error) {
      console.log('Audio not available:', error);
    }
  };
  
  const animateParticles = (initialParticles: Particle[]) => {
    const duration = 2000; // 2 seconds
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const updatedParticles = initialParticles.map(particle => ({
        ...particle,
        x: particle.x + particle.vx * (elapsed / 16), // Smooth movement
        y: particle.y + particle.vy * (elapsed / 16) + 0.05 * Math.pow(elapsed / 16, 2), // Gravity
        rotation: particle.rotation + particle.rotationSpeed * (elapsed / 16),
        opacity: Math.max(0, 1 - Math.pow(progress, 2)),
        scale: particle.scale * (1 + progress * 0.2) // Slight scale up
      }));
      
      setParticles(updatedParticles);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Animation complete
        setParticles([]);
        setIsAnimating(false);
        onComplete?.();
      }
    };
    
    requestAnimationFrame(animate);
  };
  
  if (!isAnimating || particles.length === 0) {
    return null;
  }
  
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 10000,
        overflow: 'hidden'
      }}
    >
      {/* Central burst text */}
      <div
        style={{
          position: 'absolute',
          left: `${centerX}%`,
          top: `${centerY}%`,
          transform: 'translate(-50%, -50%)',
          fontSize: '2rem',
          fontWeight: 'bold',
          color: '#FFD700',
          textShadow: '0 0 20px rgba(255, 215, 0, 0.8), 0 2px 4px rgba(0, 0, 0, 0.3)',
          animation: 'cashBurstText 2s ease-out forwards',
          fontFamily: 'Fredoka One, Arial, sans-serif'
        }}
      >
        +{amount} 💰
      </div>
      
      {/* Particle system */}
      {particles.map(particle => (
        <div
          key={particle.id}
          style={{
            position: 'absolute',
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            transform: `translate(-50%, -50%) rotate(${particle.rotation}deg) scale(${particle.scale})`,
            fontSize: '1.5rem',
            opacity: particle.opacity,
            color: particle.color,
            textShadow: `0 0 10px ${particle.color}`,
            pointerEvents: 'none',
            userSelect: 'none'
          }}
        >
          {particle.emoji}
        </div>
      ))}
      
      {/* Confetti background effect */}
      <div
        style={{
          position: 'absolute',
          left: `${centerX}%`,
          top: `${centerY}%`,
          transform: 'translate(-50%, -50%)',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,215,0,0.3) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'cashBurstGlow 2s ease-out forwards'
        }}
      />
      
      {/* CSS Animations */}
      <style>{`
        @keyframes cashBurstText {
          0% {
            transform: translate(-50%, -50%) scale(0.5);
            opacity: 0;
          }
          20% {
            transform: translate(-50%, -50%) scale(1.3);
            opacity: 1;
          }
          40% {
            transform: translate(-50%, -50%) scale(1.1);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(1) translateY(-20px);
            opacity: 0;
          }
        }
        
        @keyframes cashBurstGlow {
          0% {
            transform: translate(-50%, -50%) scale(0);
            opacity: 0.8;
          }
          30% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.6;
          }
          100% {
            transform: translate(-50%, -50%) scale(3);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default CashBurst;